import {inject, Injectable, Signal, signal, WritableSignal} from '@angular/core';
import {MAX_RECENT_SEARCH_LENGTH} from '../../../constants/max-recent-search-length.constant';
import {AppStorageService} from '../../app-storage/app-storage.service';
import {AppStorageIdEnum} from '../../../enums/app-storage-id.enum';
import {AmagnoStorageKeyEnum} from '../../../enums/amagno-storage-key.enum';
import {AppStorage} from '../../../models/app-storage.model';
import {RecentSearch} from '../../../models/search/recent-search.model';

@Injectable({
    providedIn: 'root'
})
export class RecentSearchService {
    private readonly appStorageService = inject(AppStorageService);
    private readonly recentSearches: WritableSignal<Array<RecentSearch>>;
    private readonly storage: AppStorage;

    public constructor() {
        this.storage = this.appStorageService.getStorage(AppStorageIdEnum.AMAGNO);
        this.recentSearches = signal(this.storage.get(AmagnoStorageKeyEnum.RECENT_SEARCHES) || []);
    }

    public getRecentSearchesSignal(): Signal<Array<RecentSearch>> {
        return this.recentSearches.asReadonly();
    }

    public resetRecentSearches(): void {
        this.recentSearches.set([]);
    }

    public addRecentSearch(searchTerm: string, vaultId?: string): void {
        this.recentSearches.update(recentSearches => {
            const recentSearch = recentSearches.find(search => search.searchTerm === searchTerm && search.vaultId === vaultId);
            if (recentSearch) {
                recentSearches = recentSearches.toSpliced(recentSearches.indexOf(recentSearch), 1);
            } else if (recentSearches.length >= MAX_RECENT_SEARCH_LENGTH) {
                recentSearches = recentSearches.toSpliced(MAX_RECENT_SEARCH_LENGTH - 1);
            }

            const newRecentSearch: RecentSearch = {
                vaultId,
                resultCount: recentSearch?.resultCount || 0,
                searchTerm,
            };

            return [
                newRecentSearch,
                ...recentSearches
            ];
        });

        this.storage.set(AmagnoStorageKeyEnum.RECENT_SEARCHES, this.recentSearches());
    }

    public updateRecentSearch(searchText: string, searchResultCount: number, searchId?: string, vaultId?: string): void {
        this.recentSearches.update(recentSearches => {
            const recentSearch = recentSearches.find(search => search.searchTerm === searchText && search.vaultId === vaultId);
            if (!recentSearch) {
                return recentSearches;
            }

            recentSearch.resultCount = searchResultCount;
            recentSearch.searchId = searchId;
            return [
                recentSearch,
                ...recentSearches.filter((search) => search !== recentSearch)
            ];
        });

        this.storage.set(AmagnoStorageKeyEnum.RECENT_SEARCHES, this.recentSearches());
    }
}
